import { z } from 'zod';

import { getPassportDocSchema } from '@/pages/UserAccount/Profile';

import { isCountryUkraine, isRegionKyiv, isRegionSevastopol } from '@/features/ui/ProfileChapters';
import { deliveryAddressSchema } from '@/features/ui/Steps/DeliveryAddress/model/schemas';

import {
  zodArrayOptional,
  zodArrayRequired,
  zodCheckbox,
  zodCheckboxOnlyTrue,
  zodEmail,
  zodGenderRequired,
  zodPhone,
  zodPlaceholder,
  zodSelectOption,
  zodSettlementUkrainian,
  zodStringOnlyUkrainian,
  zodStringOnlyUkrainianOptional,
  zodStringOptional,
  zodStringRequired,
  zodUkrainianName,
} from '@/shared/lib/validations';

import { YesNoSelectOptions } from '@/components/Forms/constants';

import { NamesBeforeFieldName } from '@/lib/consts';

import { DocumentTypeCode } from '@/api/types';

export const extractFormTypeSchema = z.object({
  extractFormType: zodStringRequired,
  isElectronicExtractDisabled: zodCheckbox,
  isPaperExtractDisabled: zodCheckbox,
});

export const personalDataSchemaElectronic = z
  .object({
    firstName: zodStringRequired,
    lastName: zodStringRequired,
    middleName: zodStringOptional,
    dateOfBirth: zodStringRequired,
    sex: zodGenderRequired,
    withoutCitizenship: zodSelectOption,
    citizenship: zodPlaceholder,
    email: zodEmail,
    phones: zodArrayRequired(zodPhone),
    rnokpp: zodStringOptional,
    isNameChanged: zodSelectOption,
    namesBefore: zodArrayOptional(
      z.object({
        [NamesBeforeFieldName.previousLastName]: zodUkrainianName(),
        [NamesBeforeFieldName.previousPatronymic]: zodUkrainianName().optional(),
        [NamesBeforeFieldName.previousName]: zodUkrainianName(),
      }),
    ),
  })
  .superRefine(({ citizenship, withoutCitizenship }, ctx) => {
    if (withoutCitizenship.id === YesNoSelectOptions.NO.id) {
      const result = zodSelectOption.safeParse(citizenship);
      if (!result.success) result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['citizenship'] }));
    }
  });

export const personalDataBaseSchemaPaper = personalDataSchemaElectronic.and(
  z.object({
    personPassportDocument: zodSelectOption.extend({
      code: z.nativeEnum(DocumentTypeCode),
    }),
    documents: z.any(),
    militaryViolation: zodCheckboxOnlyTrue,
  }),
);

export const personalDataSchemaPaper = personalDataBaseSchemaPaper.superRefine((data, ctx) => {
  const documentType = data.personPassportDocument.code;
  const schemaByDocumentTypeCode = getPassportDocSchema(documentType);
  const result = schemaByDocumentTypeCode?.safeParse(data.documents[documentType]);

  if (!result?.success) {
    result?.error.issues.forEach((issue) => {
      ctx.addIssue({
        ...issue,
        path: ['documents', documentType, ...issue.path],
      });
    });
  }
});

export const birthPlaceSchema = z.object({
  birthCounttry: zodStringOnlyUkrainian,
  birthRegion: zodStringOnlyUkrainianOptional,
  birthDistrict: zodStringOnlyUkrainianOptional,
  birthCity: zodSettlementUkrainian,
});

const registrationPlaceUkrainianSchema = z.object({
  registeredCountry: zodSelectOption,
  registeredRegion: zodSelectOption,
  registeredDistrict: zodSelectOption,
  registeredCity: zodSelectOption,
});

const registrationPlaceNonUkrainianSchema = z.object({
  registeredCountry: zodSelectOption,
  registeredCity: zodStringRequired,
});

export const registrationPlaceSchema = z
  .object({
    registeredCountry: zodSelectOption,
    registeredRegion: z.any(),
    registeredDistrict: z.any(),
    registeredCity: z.any(),
  })
  .superRefine((data, ctx) => {
    const { registeredCountry, registeredRegion } = data;
    const result = isCountryUkraine(registeredCountry.id)
      ? registrationPlaceUkrainianSchema.safeParse(data)
      : registrationPlaceNonUkrainianSchema.safeParse(data);

    if (!result.success) {
      result.error.issues.forEach((issue) => {
        const isKyivOrSevastopol = isRegionKyiv(registeredRegion?.id) || isRegionSevastopol(registeredRegion?.id);

        if (isKyivOrSevastopol && issue.path[0] === 'registeredDistrict') {
          return;
        }

        ctx.addIssue(issue);
      });
    }
  });

export const purposeAndExtractTypeSchema = z.object({
  requestPurpose: zodSelectOption,
  extractType: zodSelectOption,
  hasApostille: zodCheckbox,
});

export const birthAndRegistrationPlaceSchema = z.intersection(birthPlaceSchema, registrationPlaceSchema);

export const extendedCriminalRecordExtractBaseSchema = z.intersection(
  z.intersection(extractFormTypeSchema, personalDataSchemaPaper),
  z.intersection(z.intersection(birthAndRegistrationPlaceSchema, purposeAndExtractTypeSchema), deliveryAddressSchema),
);

export const extendedCriminalRecordExtractSchema = extendedCriminalRecordExtractBaseSchema.superRefine(
  (fields, ctx) => {
    const { citizenship, withoutCitizenship } = fields as z.infer<typeof personalDataSchemaPaper>;

    if (withoutCitizenship.id === YesNoSelectOptions.NO.id) {
      const citizenshipResult = zodSelectOption.safeParse(citizenship);

      if (!citizenshipResult.success) {
        citizenshipResult?.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['citizenship'],
          });
        });
      }
    }
  },
);
